import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getCustomerPaymentCheckout } from "../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { loadCashfree, getCashfreeMode } from "../../../Utils/cashfree";

type CheckoutData = {
  payment_id?: number;
  order_id?: string;
  status?: string;
  order_status?: string;
  amount?: number;
  currency?: string;
  payment_session_id?: string | null;
  checkout_mode?: string;
  payment_provider?: string;
  already_paid?: boolean;
  description?: string;
  customer_name?: string;
};

type PageState = "loading" | "checkout" | "paid" | "error";

const CustomerPaymentPage: React.FC = () => {
  const { secret_token } = useParams<{ secret_token: string }>();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Inject spinner keyframe animation
  useEffect(() => {
    const styleId = "customer-payment-spinner";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }, []);

  const fetchCheckout = useCallback(async (token: string) => {
    setPageState("loading");
    setErrorMsg("");
    try {
      const data = await getCustomerPaymentCheckout(token);

      if (!data) {
        setErrorMsg("Payment link is invalid or expired.");
        setPageState("error");
        return;
      }

      setCheckoutData(data);

      if (data.already_paid || data.status === "paid") {
        setPageState("paid");
        return;
      }

      if (data.payment_session_id) {
        setPageState("checkout");
        // Launch Cashfree checkout in modal (not redirect) so customer stays on this page
        try {
          const mode = getCashfreeMode();
          const cashfree = await loadCashfree(mode);
          const result = await cashfree.checkout({
            paymentSessionId: data.payment_session_id,
            redirectTarget: "_modal",
          });
          console.log("Cashfree checkout result:", result);
        } catch (checkoutErr: any) {
          console.error("Cashfree checkout closed/error:", checkoutErr);
        }
        // After modal closes (success, failure, or user dismissed), re-check status
        const recheck = await getCustomerPaymentCheckout(token);
        if (recheck && (recheck.already_paid || recheck.status === "paid")) {
          setCheckoutData(recheck);
          setPageState("paid");
        } else {
          setErrorMsg("Payment was not completed. Please try again.");
          setPageState("error");
        }
      } else {
        setErrorMsg("Payment session expired. Please request a new link from the studio.");
        setPageState("error");
      }
    } catch (err: any) {
      console.error("Checkout fetch error:", err);
      setErrorMsg("Something went wrong. Please try again.");
      setPageState("error");
    }
  }, []);

  useEffect(() => {
    if (!secret_token) {
      setErrorMsg("Invalid payment link.");
      setPageState("error");
      return;
    }
    fetchCheckout(secret_token);
  }, [secret_token, fetchCheckout]);

  // Loading state
  if (pageState === "loading") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={spinnerStyle} />
          <h2 style={{ margin: "16px 0 8px", fontSize: "18px", color: "#374151" }}>
            Loading payment details...
          </h2>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Please wait</p>
        </div>
      </div>
    );
  }

  // Already paid
  if (pageState === "paid") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={successIconStyle}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#16a34a"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 style={{ margin: "16px 0 8px", fontSize: "24px", fontWeight: 700, color: "#111827" }}>
            Payment Completed!
          </h1>
          {checkoutData?.amount != null && (
            <p style={{ fontSize: "22px", fontWeight: 600, color: "#16a34a", margin: "4px 0" }}>
              ₹{checkoutData.amount}
            </p>
          )}
          {checkoutData?.customer_name && (
            <p style={{ color: "#6b7280", margin: "4px 0" }}>{checkoutData.customer_name}</p>
          )}
          <p style={{ color: "#6b7280", textAlign: "center", maxWidth: "320px", marginTop: "8px" }}>
            Your payment has been successfully processed. Thank you!
          </p>
        </div>
      </div>
    );
  }

  // Checkout state (Cashfree SDK is redirecting)
  if (pageState === "checkout") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={spinnerStyle} />
          <h2 style={{ margin: "16px 0 8px", fontSize: "18px", color: "#374151" }}>
            Redirecting to payment gateway...
          </h2>
          {checkoutData?.amount != null && (
            <p style={{ color: "#6b7280", fontSize: "14px" }}>Amount: ₹{checkoutData.amount}</p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={errorIconStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#dc2626"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 style={{ margin: "16px 0 8px", fontSize: "24px", fontWeight: 700, color: "#111827" }}>
          Payment Error
        </h1>
        <p style={{ color: "#6b7280", textAlign: "center", maxWidth: "320px", margin: "0 0 16px" }}>
          {errorMsg || "Something went wrong."}
        </p>
        <button
          onClick={() => secret_token && fetchCheckout(secret_token)}
          style={retryButtonStyle}
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

export default CustomerPaymentPage;

// ── Inline styles (no Tailwind / styled-components dependency) ──────

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  background: "#f9fafb",
  padding: "16px",
  fontFamily: "Urbanist, Inter, Arial, sans-serif",
};

const cardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "#ffffff",
  borderRadius: "16px",
  padding: "32px 24px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  maxWidth: "420px",
  width: "100%",
};

const spinnerStyle: React.CSSProperties = {
  width: "48px",
  height: "48px",
  border: "4px solid #e5e7eb",
  borderTopColor: "#a855f7",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const successIconStyle: React.CSSProperties = {
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  background: "#dcfce7",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const errorIconStyle: React.CSSProperties = {
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  background: "#fee2e2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const retryButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  background: "linear-gradient(to right, #FFC951, #C94DAA)",
  color: "#ffffff",
  border: "none",
  borderRadius: "8px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
};
